// The country-aware address section (app/views/shared/_address_fields.html.erb):
// the subdivision control and label, and the postal-code label and hint, follow
// the address's country as the visitor changes it.
//
// The server renders the section for the country it was given, so the section
// is right before this runs and without it. This file holds no geography and no
// wording. The section carries, per country, what the server would render
// (AddressFieldsHelper), and the script looks the country up:
//
//   data-address-fields          marks the section
//   data-address-variants        {"USA": {...}, "CAN": {...}, ..., "default": {...}},
//                                each with subdivision_label, postal_label,
//                                postal_hint, and options and selected for a
//                                country with a subdivision list
//   data-address-country         the country the server rendered, followed when
//                                there is no country control
//   data-address-country-field   id of the country select to follow
//   data-address-non-us-field    id of a checkbox whose unchecked state means
//                                the country in data-address-us-country
//                                (Signup's "outside the United States" box)
//
// Both subdivision controls are rendered with the same name, each in its own
// container. The container that does not apply is hidden, and its control
// carries `data-disable-when-hidden`, so it neither submits nor validates.
//
// Tier-1 vanilla, like signup.js: handlers are delegated from `document` and
// every behavior is opted into by a data attribute.

import { syncConcealedControls } from "./concealed_controls.js";

const DEFAULT_VARIANT = "default";

// The visitor's last pick per country, per select, so going back to a country
// restores it. A pick never carries into another country's list: codes repeat
// across countries (WA is Washington and Western Australia).
const picks = new WeakMap();

const parseVariants = (section) => {
  try {
    return JSON.parse(section.dataset.addressVariants);
  } catch (e) {
    return null;
  }
};

const controlById = (id) => (id ? document.getElementById(id) : null);

const effectiveCountry = (section) => {
  const nonUs = controlById(section.dataset.addressNonUsField);
  if (nonUs && !nonUs.checked) return section.dataset.addressUsCountry;

  const country = controlById(section.dataset.addressCountryField);
  return country ? country.value : section.dataset.addressCountry;
};

// Saves the pick for the country whose options are loaded, and returns every
// pick saved for this select.
const rememberPick = (select) => {
  const remembered = picks.get(select) || {};
  const loaded = select.dataset.addressOptionsFor;
  if (loaded) remembered[loaded] = select.value;
  picks.set(select, remembered);
  return remembered;
};

// Keeps the server's blank option, so a required select still opens on no
// choice rather than on its first entry.
const replaceOptions = (select, options) => {
  const blank = select.querySelector('option[value=""]');
  select.length = 0;
  if (blank) select.add(blank);
  options.forEach(([label, value]) => select.add(new Option(label, value)));
};

const loadOptions = (select, key, variant) => {
  if (select.dataset.addressOptionsFor === key) return;

  const remembered = rememberPick(select);
  replaceOptions(select, variant.options);
  select.value = key in remembered ? remembered[key] : variant.selected || "";
  select.dataset.addressOptionsFor = key;
};

const variantKey = (variants, country) =>
  Object.prototype.hasOwnProperty.call(variants, country) ? country : DEFAULT_VARIANT;

const setText = (section, selector, text) => {
  section.querySelectorAll(selector).forEach((el) => {
    el.textContent = text || "";
  });
};

const applyWording = (section, variant) => {
  setText(section, "[data-address-subdivision-label]", variant.subdivision_label);
  setText(section, "[data-address-postal-label]", variant.postal_label);
  setText(section, "[data-address-postal-hint]", variant.postal_hint);
  section.querySelectorAll("[data-address-postal-hint]").forEach((hint) => {
    hint.hidden = !variant.postal_hint;
  });
};

const showSubdivisionControl = (section, key, variant) => {
  const listed = Array.isArray(variant.options);
  const selectBox = section.querySelector("[data-address-subdivision-select]");
  const textBox = section.querySelector("[data-address-subdivision-text]");

  if (listed) loadOptions(selectBox.querySelector("select"), key, variant);
  selectBox.hidden = !listed;
  textBox.hidden = listed;
  syncConcealedControls(section);
};

const updateAddressSection = (section) => {
  const variants = parseVariants(section);
  if (!variants) return;

  const key = variantKey(variants, effectiveCountry(section));
  applyWording(section, variants[key]);
  showSubdivisionControl(section, key, variants[key]);
};

const sectionsFollowing = (control) =>
  Array.from(document.querySelectorAll("[data-address-fields]")).filter(
    (section) => section.dataset.addressCountryField === control.id || section.dataset.addressNonUsField === control.id,
  );

document.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.id) return;

  sectionsFollowing(target).forEach(updateAddressSection);
});

// Also on load, not just on change: a browser that restores form state on a
// reload or a back navigation can bring back a country other than the one the
// server rendered for.
const initAddressSections = () => {
  document.querySelectorAll("[data-address-fields]").forEach(updateAddressSection);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAddressSections);
} else {
  initAddressSections();
}
